import { lazy, Suspense, useEffect, useRef } from 'react'
import { Navigate, Routes, Route, useLocation } from 'react-router-dom'
import { Layout } from './components/Layout'
import { CmsLayout } from './components/CmsLayout'
import { PublicLayout } from './components/layout/PublicLayout'
import { Preloader } from './components/layout/Preloader'
import { PortalLogin } from './pages/PortalLogin'
import { PortalDashboard } from './pages/PortalDashboard'
import { ComingSoon } from './pages/ComingSoon'
import { useBrandFavicon, useFrontendMode } from './services/httpServices/queries/useWebsite'
import { CmsDashboard } from './pages/cms/Dashboard'
import { CmsPages } from './pages/cms/Pages'
import { CmsDoctors } from './pages/cms/Doctors'
import { CmsMedia } from './pages/cms/Media'
import { CmsSettings } from './pages/cms/Settings'

const HomeThree = lazy(() => import('./pages/website/HomeThree').then((m) => ({ default: m.HomeThree })))
const About = lazy(() => import('./pages/website/About').then((m) => ({ default: m.About })))
const Services = lazy(() => import('./pages/website/Services').then((m) => ({ default: m.Services })))
const ServiceDetail = lazy(() =>
  import('./pages/website/ServiceDetail').then((m) => ({ default: m.ServiceDetail })),
)
const Appointment = lazy(() => import('./pages/website/Appointment').then((m) => ({ default: m.Appointment })))
const Doctors = lazy(() => import('./pages/website/Doctors').then((m) => ({ default: m.Doctors })))
const DoctorDetail = lazy(() =>
  import('./pages/website/DoctorDetail').then((m) => ({ default: m.DoctorDetail })),
)
const Portfolio = lazy(() => import('./pages/website/Portfolio').then((m) => ({ default: m.Portfolio })))
const PortfolioDetails = lazy(() =>
  import('./pages/website/PortfolioDetails').then((m) => ({ default: m.PortfolioDetails })),
)
const Gallery = lazy(() => import('./pages/website/Gallery').then((m) => ({ default: m.Gallery })))
const Testimonials = lazy(() => import('./pages/website/Testimonials').then((m) => ({ default: m.Testimonials })))
const Faq = lazy(() => import('./pages/website/Faq').then((m) => ({ default: m.Faq })))
const ErrorPage = lazy(() => import('./pages/website/ErrorPage').then((m) => ({ default: m.ErrorPage })))
const SignIn = lazy(() => import('./pages/website/SignIn').then((m) => ({ default: m.SignIn })))
const Register = lazy(() => import('./pages/website/Register').then((m) => ({ default: m.Register })))
const ForgotPassword = lazy(() =>
  import('./pages/website/ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
)
const Blog = lazy(() => import('./pages/website/Blog').then((m) => ({ default: m.Blog })))
const BlogArchive = lazy(() =>
  import('./pages/website/BlogArchive').then((m) => ({ default: m.BlogArchive })),
)
const BlogDetail = lazy(() => import('./pages/website/BlogDetail').then((m) => ({ default: m.BlogDetail })))
const Contact = lazy(() => import('./pages/website/Contact').then((m) => ({ default: m.Contact })))
const ReportCheck = lazy(() => import('./pages/website/ReportCheck').then((m) => ({ default: m.ReportCheck })))
const Terms = lazy(() => import('./pages/website/Terms').then((m) => ({ default: m.Terms })))
const Privacy = lazy(() => import('./pages/website/Privacy').then((m) => ({ default: m.Privacy })))

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <span className="text-primary" aria-label="Loading">
        <i className="fa-solid fa-circle-notch fa-spin text-3xl" />
      </span>
    </div>
  )
}

function App() {
  const { isDevelopment, isLoading } = useFrontendMode()
  useBrandFavicon()
  const { pathname } = useLocation()

  // The branded preloader lives at the app root so the first React paint (and
  // the settings/mode fetch plus the lazy route chunk load) is covered by one
  // loader instead of a plain spinner followed by the branded preloader.
  // It is only needed when the app boots on a public route and stays mounted
  // (faded out) afterwards, so SPA navigation never re-shows it.
  const isPublicRoute = !pathname.startsWith('/portal') && !pathname.startsWith('/cms')
  const bootOnPublicRoute = useRef(isPublicRoute).current

  // Development mode swaps the public site for the "coming soon" placeholder,
  // which never mounts PublicLayout, so fade the preloader here in that case.
  useEffect(() => {
    if (!isLoading && isDevelopment) {
      window.__fastaidPreloader?.()
    }
  }, [isLoading, isDevelopment])

  // While the mode is loading show the spinner; when the site is in development
  // mode every public marketing route renders the under-development placeholder.
  const publicElement = isLoading ? (
    <RouteFallback />
  ) : isDevelopment ? (
    <ComingSoon />
  ) : (
    <PublicLayout />
  )

  return (
    <>
      {bootOnPublicRoute && <Preloader />}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Keep portal and CMS accessible for development */}
          <Route element={<Layout />}>
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal" element={<PortalDashboard />} />
          </Route>

          {/* CMS Routes (nested under /cms) */}
          <Route path="/cms" element={<CmsLayout />}>
            <Route index element={<CmsDashboard />} />
            <Route path="pages" element={<CmsPages />} />
            <Route path="doctors" element={<CmsDoctors />} />
            <Route path="media" element={<CmsMedia />} />
            <Route path="settings" element={<CmsSettings />} />
          </Route>

          {/* Public marketing website */}
          <Route element={publicElement}>
            <Route index element={<HomeThree />} />
            <Route path="about" element={<About />} />
            <Route path="services" element={<Services />} />
            <Route path="services/:slug" element={<ServiceDetail />} />
            {/* legacy generic detail link used by older template markup */}
            <Route path="services-details" element={<Navigate to="/services" replace />} />
            <Route path="appointment" element={<Appointment />} />
            <Route path="doctors" element={<Doctors />} />
            <Route path="doctors/:slug" element={<DoctorDetail />} />
            {/* legacy generic detail link used by older template markup */}
            <Route path="doctor-details" element={<Navigate to="/doctors" replace />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="portfolio-details" element={<PortfolioDetails />} />
            <Route path="image-gallery" element={<Gallery initialTab="image" />} />
            <Route path="video-gallery" element={<Gallery initialTab="video" />} />
            <Route path="testimonials" element={<Testimonials />} />
            <Route path="faq" element={<Faq />} />
            <Route path="error" element={<ErrorPage />} />
            <Route path="sign-in" element={<SignIn />} />
            <Route path="register" element={<Register />} />
            <Route path="forget-password" element={<ForgotPassword />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/category/:category" element={<BlogArchive kind="category" />} />
            <Route path="blog/tag/:tag" element={<BlogArchive kind="tag" />} />
            <Route path="blog/:slug" element={<BlogDetail />} />
            {/* legacy generic detail link used by older template markup */}
            <Route path="blog-details" element={<Navigate to="/blog" replace />} />
            <Route path="contact" element={<Contact />} />
            <Route path="terms-and-conditions" element={<Terms />} />
            <Route path="privacy-policy" element={<Privacy />} />
            <Route path="report-check" element={<ReportCheck />} />
            <Route path="*" element={<ErrorPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}

export default App
